import type { Span } from './types';

/** Detect descendants before reparenting so explicit ownership cannot introduce a cycle. */
function containsSpan(parent: Span, target: Span): boolean {
  return (
    parent === target ||
    (parent.children ?? []).some((child) => containsSpan(child, target))
  );
}

/** Read the recorded parent tool call from a procedure or its tagged descendants. */
function ownerOf(span: Span): string | undefined {
  const owner = span.toolInput?.parentToolCallId;
  if (typeof owner === 'string') return owner;
  for (const child of span.children ?? []) {
    const nested = ownerOf(child);
    if (nested) return nested;
  }
  return undefined;
}

/** New traces carry explicit ownership. Missing ownership never falls back to timing. */
export function attachOwnedProcedures(spans: Span[]): Span[] {
  const hosts = new Map<string, Span>();
  /** Clone the tree before moving procedures and index the possible owning tool calls. */
  function copy(list: Span[]): Span[] {
    return list.map((span) => {
      const result = {
        ...span,
        children: span.children ? copy(span.children) : undefined,
      };
      if (result.toolCallId) hosts.set(result.toolCallId, result);
      return result;
    });
  }
  const roots = copy(spans);
  const moves: Array<{ procedure: Span; host: Span }> = [];
  /** Remove explicitly owned procedures from their old parents and collect safe moves. */
  function detach(list: Span[], parent?: Span): Span[] {
    return list.filter((span) => {
      if (span.children) span.children = detach(span.children, span);
      if (!span.isSopProcedure) return true;
      const owner = ownerOf(span);
      const host = owner ? hosts.get(owner) : undefined;
      if (!host || host === parent || containsSpan(span, host)) return true;
      moves.push({ procedure: span, host });
      return false;
    });
  }
  const result = detach(roots);
  for (const { procedure, host } of moves) {
    host.children = [...(host.children ?? []), procedure].sort(
      (a, b) => a.startMs - b.startMs,
    );
  }
  return result;
}

/** Recorded per-task outcomes, independent of today's mutable task state. */
export function recordedTaskOutcomes(spans: Span[]): Span[] {
  return spans.flatMap((span) =>
    span.taskOutcome ? [span] : recordedTaskOutcomes(span.children ?? []),
  );
}

/** A task the loop has started and not yet finished, so it has no outcome yet. */
function isInProgressTask(span: Span): boolean {
  return (
    span.status === 'running' &&
    span.isPhase === true &&
    span.action === 'agent_task'
  );
}

/**
 * A task the loop started but never reached a verdict on: its execution threw
 * (a model or infrastructure error), so the loop released it back to the queue
 * and recorded the error on the task span instead of an outcome.
 */
export function isUnfinishedTask(span: Span): boolean {
  return (
    span.status === 'failed' &&
    !span.taskOutcome &&
    span.isPhase === true &&
    span.action === 'agent_task'
  );
}

/**
 * Tasks listed on a run's Details tab: saved outcomes, the task still running,
 * and a task that stopped with an error. Neither of the last two has an outcome
 * row, but each was worked on in this run.
 */
export function taskDetailSpans(spans: Span[]): Span[] {
  return spans.flatMap((span) =>
    span.taskOutcome || isInProgressTask(span) || isUnfinishedTask(span)
      ? [span]
      : taskDetailSpans(span.children ?? []),
  );
}
